import React, { useState } from "react";
import PropTypes from "prop-types";
import classnames from "classnames";

import "./tooltip.scss";
import Icon from "../Icon/index";

const Tooltip = ({
  heading,
  content,
  className,
  delay,
  direction,
  children,
  showTooltipOnLoad,
}) => {
  let timeout;

  const tooltipKey = `tooltip--${
    new Date().getTime() + (Math.random() + 1).toString(36).substring(7)
  }`;

  const [active, setActive] = useState(showTooltipOnLoad);
  const [activeFirstTime, setActiveFirstTime] = useState(showTooltipOnLoad);

  const toggleTooltip = () => {
    if (active) {
      hideTip();
    } else {
      showTip();
    }
  };

  const closeTooltipOnKeyDown = (ev) => {
    // When user press Enter/Space, close the tooltip
    if (ev.which === 13 || ev.which === 32) {
      ev.preventDefault();
      closeTip();
    }
  };

  const toggleTooltipOnKeyUp = (ev) => {
    // When user press Enter/Space, toggle the tooltip
    if (ev.which === 13 || ev.which === 32) {
      ev.preventDefault();
      showTip();
    }
    // When user press escape, hide the tooltip
    if (ev.which === 27) {
      ev.preventDefault();
      hideTip();
    }
  };

  const showTip = () => {
    timeout = setTimeout(() => {
      setActive(true);
    }, delay);
  };

  const hideTip = () => {
    if (!activeFirstTime) {
      clearInterval(timeout);
      setActive(false);
    }
  };

  const closeTip = () => {
    clearInterval(timeout);
    setActive(false);
    setActiveFirstTime(false);
  };

  return (
    <div className={classnames("tooltip", className)}>
      <span
        className="tooltip__btn"
        onMouseEnter={showTip}
        onMouseLeave={hideTip}
        onKeyDown={toggleTooltipOnKeyUp}
        onClick={toggleTooltip}
        onFocus={showTip}
        onBlur={hideTip}
        aria-label="tooltip"
        aria-describedby={active ? tooltipKey : null}
        role="button"
        tabIndex={0}
      >
        {children ? (
          children
        ) : (
          <Icon
            className="tooltip__icon"
            name={`system-help${active ? "-filled" : ""}`}
            size="medium"
            theme="system"
          />
        )}
      </span>
      {active && (
        <div
          className={`tooltip__content ${direction || "bottom"}`}
          aria-live={active ? "assertive" : ""}
          role="tooltip"
          id={tooltipKey}
        >
          {heading && <h3 className={`tooltip__content-heading`}>{heading}</h3>}
          {content && <div className={`tooltip__content-body`}>{content}</div>}
          {activeFirstTime && (
            <button
              className="tooltip__close-btn"
              onPointerDown={closeTip}
              aria-label="click to close the tooltip"
              onKeyDown={closeTooltipOnKeyDown}
            >
              <Icon
                className="tooltip__icon-close"
                name="system-close"
                size="medium"
                theme="system"
              />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

Tooltip.propTypes = {
  heading: PropTypes.string,
  content: PropTypes.node,
  direction: PropTypes.oneOf(["top", "left", "right", "bottom"]),
  className: PropTypes.string,
  delay: PropTypes.number,
  children: PropTypes.node,
  showTooltipOnLoad: PropTypes.bool,
};

Tooltip.defaultProps = {
  direction: "right",
  showTooltipOnLoad: false,
  delay: 25,
};

export default Tooltip;
